import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db, functions } from "../Firebase/firebaseconfig";
import { httpsCallable } from "firebase/functions";
import notify from "./common/notify";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import "./Dashboard.css";
import { useUserContext } from "./GestionDeUsuarios/UserContext";
import { construirResumenFinanciero } from "../helpers/finanzasDeuda";
import { construirResumenCobranza } from "../helpers/resumenCobranza";
import { toJsDate } from "../helpers/fechas";
import { obtenerResumenOperativo } from "../services/cobranzaOperativaService";
import {
  listarSnapshotsRecientes,
  calcularDeltas,
} from "../services/cobranzaSnapshotsService";
import ApartadosPorVencerBanner from "./Apartados/ApartadosPorVencerBanner";
import RecobrosPendientesBanner from "./Comisiones/RecobrosPendientesBanner";
import ReporteEjecutivoBanner from "./Dashboard/ReporteEjecutivoBanner";
import PlanDiaBanner from "./Dashboard/PlanDiaBanner";
import { IA_FLAGS } from "../helpers/iaFlags";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/* =========================
   Helpers
========================= */

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toDateSafe = (input) => toJsDate(input);

// KPI ejecutivo: $1,500,000 sin .00 si es entero (legibilidad).
const formatMoney = (amount) =>
  `$${toNumber(amount).toLocaleString("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

// Pago individual: SIEMPRE 2 decimales (CV-3). Es lo que ve el cliente al
// verificar un pago, no tolera ambigüedad ni redondeo visual.
const formatMoneyMx = (amount) =>
  `$${toNumber(amount).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} MXN`;

const formatDate = (value) => {
  const d = toDateSafe(value);
  if (!d) return "—";
  return d.toLocaleDateString("es-MX");
};

const getPagosArray = (data) => {
  if (Array.isArray(data?.pagos)) return data.pagos;
  if (data?.pagos && typeof data.pagos === "object") {
    return Object.values(data.pagos);
  }
  return [];
};

const getNombreCompleto = (persona = {}) =>
  [persona?.nombre, persona?.apellidos].filter(Boolean).join(" ").trim();

const extraerPagoId = (raw) => {
  if (!raw) return null;

  let v = String(raw).trim();
  v = v.replace(/[;\r\n]+$/, "");

  if (v.startsWith("P:")) v = v.slice(2);

  const partes = v.split(":").filter(Boolean);
  const pagoId = partes[partes.length - 1];

  return pagoId || null;
};

const calcularTicketsStats = (ticketsDocs, ticketsUrgentesSnap) => {
  let abiertos = 0;
  let bloqueados = 0;
  let urgentes = 0;
  const urgentesTemp = [];

  ticketsDocs.forEach((tRef) => {
    const t = tRef.data();
    const estado = String(t?.estado || "");
    const prioridad = String(t?.prioridad || "");

    if (
      estado === "abierto" ||
      estado === "en_progreso" ||
      estado === "bloqueado"
    ) {
      abiertos += 1;
    }

    if (estado === "bloqueado") {
      bloqueados += 1;
    }

    if (prioridad === "urgente" || prioridad === "alta") {
      urgentes += 1;
    }
  });

  if (ticketsUrgentesSnap) {
    ticketsUrgentesSnap.docs.forEach((tRef) => {
      const t = tRef.data();
      urgentesTemp.push({
        titulo: t?.titulo || t?.asunto || "Ticket",
        estado: t?.estado || "",
        prioridad: t?.prioridad || "",
      });
    });
  } else {
    ticketsDocs.forEach((tRef) => {
      const t = tRef.data();
      const prioridad = String(t?.prioridad || "");

      if (prioridad === "urgente" || prioridad === "alta") {
        urgentesTemp.push({
          titulo: t?.titulo || t?.asunto || "Ticket",
          estado: t?.estado || "",
          prioridad: t?.prioridad || "",
        });
      }
    });
  }

  return {
    ticketsAbiertos: abiertos,
    ticketsBloqueados: bloqueados,
    ticketsUrgentes: urgentes,
    ticketsUrgentesLista: urgentesTemp.slice(0, 3),
  };
};

const getHealthState = (enMoraCount, enMoraTotal) => {
  if (enMoraCount >= 8) {
    return {
      healthClass: "bad",
      healthText: "⚠ Mora alta. Atención urgente.",
      healthDetail: `${enMoraCount} ${
        enMoraCount === 1 ? "cuenta en mora" : "cuentas en mora"
      } · ${formatMoneyMx(enMoraTotal)}`,
    };
  }

  if (enMoraCount >= 3) {
    return {
      healthClass: "warn",
      healthText: "🔸 Hay clientes atrasados.",
      healthDetail: `${enMoraCount} ${
        enMoraCount === 1 ? "cuenta en mora" : "cuentas en mora"
      } · ${formatMoneyMx(enMoraTotal)}`,
    };
  }

  return {
    healthClass: "good",
    healthText: "✅ Todo en orden hoy.",
    healthDetail: `${enMoraCount} ${
      enMoraCount === 1 ? "cuenta en mora" : "cuentas en mora"
    } · ${formatMoneyMx(enMoraTotal)}`,
  };
};

/* =========================
   Subcomponentes
========================= */

const KpiCard = ({ label, value, className = "", onClick }) => (
  <div
    className={`dash-kpi-card ${onClick ? "clickable" : ""}`}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={(e) => {
      if (!onClick) return;
      if (e.key === "Enter" || e.key === " ") onClick();
    }}
  >
    <div className="dash-kpi-label">{label}</div>
    <div className={`dash-kpi-value ${className}`}>{value}</div>
  </div>
);

const SearchCard = ({
  title,
  placeholder,
  value,
  onChange,
  onKeyDown,
  loading,
  error,
}) => (
  <div className="buscador-card">
    <div className="buscador-label">
      <FaSearch /> {title}
    </div>

    <input
      type="text"
      className="buscador-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
    />

    {loading && <span className="buscador-status">Procesando...</span>}
    {error && <span className="buscador-error">{error}</span>}
  </div>
);

const FinancialList = ({ title, items, emptyText, renderItem }) => (
  <div className="financial-list-card">
    <div className="financial-list-head">
      <h4>{title}</h4>
      <span>{items.length}</span>
    </div>

    {items.length === 0 ? (
      <div className="financial-empty">{emptyText}</div>
    ) : (
      <div className="financial-list">{items.map(renderItem)}</div>
    )}
  </div>
);

/* =========================
   Componente principal
========================= */

const Dashboard = () => {
  const navigate = useNavigate();
  const { can } = useUserContext();

  const [userData, setUserData] = useState(null);
  // Reporte ejecutivo: refrescar el banner tras regenerar (remonta por key).
  const [reporteRefreshKey, setReporteRefreshKey] = useState(0);
  const [regenerandoReporte, setRegenerandoReporte] = useState(false);

  const regenerarReporteEjecutivo = useCallback(async () => {
    setRegenerandoReporte(true);
    try {
      const fn = httpsCallable(functions, "regenerarReporteEjecutivo");
      await fn({}); // sin args = día de hoy (MX) por defecto
      setReporteRefreshKey((k) => k + 1); // remonta el banner → re-fetch
      notify.success("Reporte ejecutivo regenerado");
    } catch (e) {
      notify.error(`No se pudo regenerar el reporte: ${e?.message || e}`);
    } finally {
      setRegenerandoReporte(false);
    }
  }, []);

  const [cobradoHoy, setCobradoHoy] = useState(0);
  const [porCobrarHoy, setPorCobrarHoy] = useState(0);
  const [enMoraCount, setEnMoraCount] = useState(0);
  const [enMoraTotal, setEnMoraTotal] = useState(0);

  const [ticketsAbiertos, setTicketsAbiertos] = useState(0);
  const [ticketsBloqueados, setTicketsBloqueados] = useState(0);
  const [ticketsUrgentes, setTicketsUrgentes] = useState(0);
  const [ticketsUrgentesLista, setTicketsUrgentesLista] = useState([]);
  const [financiero, setFinanciero] = useState(() =>
    construirResumenFinanciero([], new Date())
  );
  const [cobranza, setCobranza] = useState(() =>
    construirResumenCobranza([], new Date())
  );
  const [operativo, setOperativo] = useState({
    promesasPendientes: 0,
    promesasIncumplidas: 0,
    montoPrometidoPendiente: 0,
    gestionesHoy: 0,
  });

  const [buscarCliente, setBuscarCliente] = useState("");
  const [buscarDeuda, setBuscarDeuda] = useState("");
  const [buscarPago, setBuscarPago] = useState("");

  const [loadingCliente, setLoadingCliente] = useState(false);
  const [loadingDeuda, setLoadingDeuda] = useState(false);
  const [loadingPago, setLoadingPago] = useState(false);

  const [errorCliente, setErrorCliente] = useState("");
  const [errorDeuda, setErrorDeuda] = useState("");
  const [errorPago, setErrorPago] = useState("");

  const [pagoVerificado, setPagoVerificado] = useState(null);

  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [snapshots, setSnapshots] = useState([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const fechaLegible = useMemo(
    () =>
      now.toLocaleDateString("es-MX", {
        weekday: "long",
        day: "numeric",
        month: "short",
      }),
    [now]
  );

  const horaLegible = useMemo(
    () =>
      now.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    [now]
  );

  const cargarDashboard = useCallback(async () => {
    setLoading(true);

    try {
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      const hoy = new Date();

      const userPromise = uid
        ? getDoc(doc(db, "usuarios", uid))
        : Promise.resolve(null);

      const deudasPromise = can("deudas.ver")
        ? getDocs(collection(db, "deudas"))
        : Promise.resolve({ docs: [] });

      const ticketsUrgentesPromise = can("tickets.ver")
        ? getDocs(
            query(
              collection(db, "tickets"),
              where("prioridad", "in", ["urgente", "alta"]),
              limit(10)
            )
          ).catch(() => null)
        : Promise.resolve(null);

      const ticketsAllPromise = can("tickets.ver")
        ? getDocs(collection(db, "tickets"))
        : Promise.resolve({ docs: [] });

      const operativoPromise =
        can("cobranza.promesas.ver") || can("cobranza.gestiones.ver")
          ? obtenerResumenOperativo(hoy).catch(() => ({
              promesasPendientes: 0,
              promesasIncumplidas: 0,
              montoPrometidoPendiente: 0,
              gestionesHoy: 0,
            }))
          : Promise.resolve({
              promesasPendientes: 0,
              promesasIncumplidas: 0,
              montoPrometidoPendiente: 0,
              gestionesHoy: 0,
            });

      const snapshotsPromise = can("deudas.ver")
        ? listarSnapshotsRecientes(12).catch(() => [])
        : Promise.resolve([]);

      const [userDoc, deudasSnap, ticketsUrgentesSnap, ticketsSnap, operativoStats, snaps] =
        await Promise.all([
          userPromise,
          deudasPromise,
          ticketsUrgentesPromise,
          ticketsAllPromise,
          operativoPromise,
          snapshotsPromise,
        ]);

      setSnapshots(snaps);

      if (userDoc?.exists()) {
        setUserData(userDoc.data());
      } else {
        setUserData(null);
      }

      const cobranzaStats = construirResumenCobranza(deudasSnap.docs, hoy);
      const financieroStats = construirResumenFinanciero(deudasSnap.docs, hoy);

      setCobradoHoy(cobranzaStats.resumen.cobradoHoy);
      setPorCobrarHoy(cobranzaStats.resumen.porCobrarHoy);
      setEnMoraCount(cobranzaStats.resumen.cuentasEnMora);
      setEnMoraTotal(cobranzaStats.resumen.totalMoraReal);
      setFinanciero(financieroStats);
      setCobranza(cobranzaStats);

      const ticketStats = calcularTicketsStats(
        ticketsSnap.docs,
        ticketsUrgentesSnap
      );

      setTicketsAbiertos(ticketStats.ticketsAbiertos);
      setTicketsBloqueados(ticketStats.ticketsBloqueados);
      setTicketsUrgentes(ticketStats.ticketsUrgentes);
      setTicketsUrgentesLista(ticketStats.ticketsUrgentesLista);
      setOperativo(operativoStats);
    } catch (err) {
      console.error("Error en Dashboard.cargarDashboard:", err);
    } finally {
      setLoading(false);
    }
  }, [can]);

  useEffect(() => {
    cargarDashboard();
  }, [cargarDashboard]);

  const handleBuscarCliente = useCallback(
    async (e) => {
      if (e.key !== "Enter") return;

      const term = buscarCliente.trim();
      if (!term) return;

      setErrorCliente("");
      setLoadingCliente(true);

      try {
        const clienteId = term.replace(/^C:/i, "").trim();
        const clienteDoc = await getDoc(doc(db, "clientes", clienteId));

        if (clienteDoc.exists()) {
          navigate(`/app/cliente/${clienteId}`);
        } else {
          setErrorCliente("❌ Cliente no encontrado");
        }
      } catch (err) {
        console.error("Error buscando cliente:", err);
        setErrorCliente("Error al buscar cliente");
      } finally {
        setLoadingCliente(false);
      }
    },
    [buscarCliente, navigate]
  );

  const handleBuscarDeuda = useCallback(
    async (e) => {
      if (e.key !== "Enter") return;

      const term = buscarDeuda.trim();
      if (!term) return;

      setErrorDeuda("");
      setLoadingDeuda(true);

      try {
        const deudaId = term.replace(/^D:/i, "").trim();
        const deudaDoc = await getDoc(doc(db, "deudas", deudaId));

        if (!deudaDoc.exists()) {
          setErrorDeuda("❌ Deuda no encontrada");
          return;
        }

        const deudaData = deudaDoc.data();
        const clienteId = deudaData?.clienteId;

        if (!clienteId) {
          setErrorDeuda("❌ Esta deuda no tiene cliente asociado");
          return;
        }

        navigate(`/app/cliente/${clienteId}/deudas/${deudaId}`);
      } catch (err) {
        console.error("Error buscando deuda:", err);
        setErrorDeuda("Error al buscar deuda");
      } finally {
        setLoadingDeuda(false);
      }
    },
    [buscarDeuda, navigate]
  );

  const handleVerificarPago = useCallback(
    async (e) => {
      if (e.key !== "Enter") return;

      const term = buscarPago.trim();
      if (!term) return;

      setErrorPago("");
      setPagoVerificado(null);
      setLoadingPago(true);

      try {
        const pagoId = extraerPagoId(term);

        if (!pagoId) {
          setErrorPago("❌ Código inválido");
          return;
        }

        const deudasSnap = await getDocs(collection(db, "deudas"));

        let deudaEncontrada = null;
        let pagoEncontrado = null;

        for (const docSnap of deudasSnap.docs) {
          const deudaData = docSnap.data();
          const pagosArr = getPagosArray(deudaData);

          const pago = pagosArr.find((p) => String(p?.pagoId || "") === String(pagoId));

          if (pago) {
            deudaEncontrada = { id: docSnap.id, ...deudaData };
            pagoEncontrado = pago;
            break;
          }
        }

        if (!pagoEncontrado || !deudaEncontrada) {
          setErrorPago("❌ Pago no encontrado en el sistema");
          return;
        }

        const clienteDoc = deudaEncontrada?.clienteId
          ? await getDoc(doc(db, "clientes", deudaEncontrada.clienteId))
          : null;

        const clienteData = clienteDoc?.exists() ? clienteDoc.data() : null;

        let cobradorNombre = "Desconocido";

        if (pagoEncontrado?.cobradorId) {
          const cobradorDoc = await getDoc(
            doc(db, "usuarios", pagoEncontrado.cobradorId)
          );

          if (cobradorDoc.exists()) {
            const cobrador = cobradorDoc.data();
            cobradorNombre =
              getNombreCompleto(cobrador) || cobrador?.email || "Usuario";
          }
        } else if (pagoEncontrado?.cobradorNombre) {
          cobradorNombre = pagoEncontrado.cobradorNombre;
        }

        setPagoVerificado({
          pago: pagoEncontrado,
          deuda: deudaEncontrada,
          cliente: clienteData
            ? {
                nombre: clienteData?.nombre || "",
                apellidos: clienteData?.apellidos || "",
                telefono: clienteData?.telefono || "",
              }
            : null,
          cobrador: cobradorNombre,
        });
      } catch (err) {
        console.error("❌ Error verificando pago:", err);
        setErrorPago("Error al verificar pago");
      } finally {
        setLoadingPago(false);
      }
    },
    [buscarPago]
  );

  const health = useMemo(
    () => getHealthState(enMoraCount, enMoraTotal),
    [enMoraCount, enMoraTotal]
  );

  const nombre = userData?.nombre || "Usuario";
  const rol = userData?.rol || "";
  const resumenFin = financiero.resumen;
  const resumenCobranza = cobranza.resumen;

  // === Datos derivados de snapshots (Sprint C integrado) ===
  const deudasFlat = useMemo(() => {
    // Reconstruir lista plana de deudas con perfil para calcular deltas
    return [
      ...cobranza.rankings.mayorMora,
      ...cobranza.rankings.criticas,
      ...cobranza.rankings.mayorRecuperabilidad,
    ].map((d) => ({
      id: d.id,
      saldoAtrasado: d.perfil?.saldoAtrasado || 0,
      saldoRestante: d.perfil?.saldoRestante || 0,
    }));
  }, [cobranza.rankings]);

  const deltas = useMemo(() => calcularDeltas(snapshots, deudasFlat), [snapshots, deudasFlat]);

  const serieTendencia = useMemo(() => {
    return [...snapshots]
      .sort((a, b) => (a.fechaMs || 0) - (b.fechaMs || 0))
      .map((s) => ({
        fecha: s.fechaISO?.slice(5) || s.id, // mm-dd más corto
        saldoAtrasado: Math.round(Number(s?.totales?.totalSaldoAtrasado || 0)),
        casos: Number(s?.totales?.totalCasos || 0),
      }));
  }, [snapshots]);

  // Distribución de cartera (del snapshot más reciente o del estado actual)
  const carteraDistribucion = useMemo(() => {
    const ultimoSnap = snapshots[0];
    const t = ultimoSnap?.totales;
    const dist = t
      ? { A: t.A || 0, B: t.B || 0, C: t.C || 0, D: t.D || 0, COLA_FINAL: t.COLA_FINAL || 0 }
      : { A: 0, B: 0, C: 0, D: 0, COLA_FINAL: 0 };
    const total = Object.values(dist).reduce((a, b) => a + b, 0) || 1;
    const colores = {
      A: "#3b82f6", B: "#f59e0b", C: "#f97316", D: "#ef4444", COLA_FINAL: "#eab308",
    };
    const labels = {
      A: "A · 1-3m", B: "B · 4-6m", C: "C · 7-9m", D: "D · 10+m", COLA_FINAL: "Cola final",
    };
    return Object.entries(dist).map(([cat, count]) => ({
      cat,
      count,
      pct: (count / total) * 100,
      color: colores[cat],
      label: labels[cat],
    }));
  }, [snapshots]);

  if (loading) {
    return <div className="dashboard-wrapper">Cargando...</div>;
  }

  // Saldo atrasado total: EN VIVO (mora real actual de la cartera), no del
  // snapshot histórico — para que concuerde con "cuentas en mora" del momento.
  // (El histórico sigue disponible en la gráfica de tendencia con snapshots.)
  const saldoAtrasadoTotal = enMoraTotal || 0;
  const tieneTendencia = serieTendencia.length >= 2;
  const tieneCarteraDist = carteraDistribucion.some((c) => c.count > 0);

  return (
    <div className="dashboard-wrapper dashboard-ejecutivo">
      {/* ═════ ZONA 1: Hero ═════ */}
      <header className="dash-hero">
        <div className="dash-hero-left">
          <div className="dash-hi">
            Hola, {nombre}
            {rol && <span className="dash-rol">({rol})</span>}
          </div>
          <div className="dash-sub">
            {fechaLegible} · {horaLegible}
          </div>
        </div>

        <div className="dash-hero-center">
          <span className="dash-hero-label">Saldo atrasado total</span>
          <strong className="dash-hero-value">
            {formatMoney(saldoAtrasadoTotal)}
          </strong>
          {deltas && (
            <div
              className={`dash-hero-delta ${deltas.totalDeltaSA < 0 ? "is-down" : deltas.totalDeltaSA > 0 ? "is-up" : ""}`}
            >
              {deltas.totalDeltaSA < 0 ? "▼" : deltas.totalDeltaSA > 0 ? "▲" : "="}{" "}
              {formatMoney(Math.abs(deltas.totalDeltaSA))} vs hace{" "}
              {deltas.diasEntre} día{deltas.diasEntre === 1 ? "" : "s"}
            </div>
          )}
        </div>

        <div className="dash-hero-right">
          <div className={`dash-hero-health ${health.healthClass}`}>
            <span className="dash-hero-health-dot" />
            <div>
              <div className="dash-hero-health-text">{health.healthText}</div>
              <div className="dash-hero-health-detail">{health.healthDetail}</div>
            </div>
          </div>
        </div>
      </header>

      {/* ═════ ZONA 2: KPIs principales ═════ */}
      {can("deudas.ver") && (
        <section className="dashboard-section">
          <div className="dash-kpi-hero-grid">
            <KpiCard
              label="Cuentas críticas"
              value={resumenCobranza.cuentasCriticas}
              className="negativo"
              onClick={() => navigate("/app/cobranza/inteligente")}
            />
            <KpiCard
              label="Potencial recuperable"
              value={formatMoney(resumenCobranza.potencialRecuperable)}
              className="positivo"
              onClick={() => navigate("/app/cobranza/inteligente")}
            />
            <KpiCard
              label="Promesas pendientes"
              value={`${operativo.promesasPendientes} (${operativo.promesasIncumplidas} venc.)`}
              className="mora"
              onClick={() => navigate("/app/cobranza/inteligente")}
            />
            <KpiCard
              label="Cobrado mes"
              value={formatMoney(resumenFin.cobradoMes)}
              className="positivo"
            />
          </div>
        </section>
      )}

      {/* ═════ Alertas estratégicas (auto-ocultan si no hay nada) ═════ */}
      {IA_FLAGS.dashboardBannerPlanDia && <PlanDiaBanner />}
      {IA_FLAGS.dashboardBannerReporteEjecutivo && (
        <>
          {can("reportes.ver") && (
            <div style={{ display: "flex", justifyContent: "flex-end", margin: "4px 0" }}>
              <button
                type="button"
                onClick={regenerarReporteEjecutivo}
                disabled={regenerandoReporte}
                className="dash-btn-regenerar-reporte"
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #c7d2fe",
                  background: regenerandoReporte ? "#e2e8f0" : "#eef2ff",
                  color: "#4338ca",
                  cursor: regenerandoReporte ? "not-allowed" : "pointer",
                }}
              >
                {regenerandoReporte ? "Generando…" : "🔄 Regenerar reporte ejecutivo"}
              </button>
            </div>
          )}
          <ReporteEjecutivoBanner key={reporteRefreshKey} />
        </>
      )}
      <ApartadosPorVencerBanner dias={7} />
      <RecobrosPendientesBanner />

      {/* ═════ ZONA 3: Tendencia + Distribución (lado a lado) ═════ */}
      {(tieneTendencia || tieneCarteraDist) && (
        <section className="dashboard-section dash-graficos-section">
          {tieneTendencia && (
            <div className="dash-grafico-card">
              <div className="section-head">
                <h3>Tendencia de cartera ({serieTendencia.length} snapshots)</h3>
                <button
                  className="mini-link"
                  onClick={() => navigate("/app/cobranza/inteligente")}
                >
                  Ver detalle →
                </button>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={serieTendencia}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="fecha" stroke="#64748b" />
                  <YAxis
                    stroke="#dc2626"
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <RTooltip formatter={(value) => formatMoney(value)} />
                  <Line
                    type="monotone"
                    dataKey="saldoAtrasado"
                    stroke="#dc2626"
                    strokeWidth={2}
                    name="Saldo atrasado"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {tieneCarteraDist && (
            <div className="dash-grafico-card">
              <div className="section-head">
                <h3>Composición de cartera</h3>
              </div>
              <div className="dash-cartera-bar">
                {carteraDistribucion
                  .filter((seg) => seg.count > 0)
                  .map((seg) => (
                    <button
                      key={seg.cat}
                      type="button"
                      className="dash-cartera-seg"
                      style={{ width: `${seg.pct}%`, background: seg.color }}
                      title={`${seg.label}: ${seg.count} (${seg.pct.toFixed(1)}%)`}
                      onClick={() =>
                        navigate(`/app/cobranza/inteligente`)
                      }
                    >
                      <span>{seg.cat === "COLA_FINAL" ? "Cola" : seg.cat}</span>
                      <strong>{seg.count}</strong>
                    </button>
                  ))}
              </div>
              <div className="dash-cartera-leyenda">
                {carteraDistribucion
                  .filter((s) => s.count > 0)
                  .map((s) => (
                    <span key={s.cat} className="dash-cartera-leyenda-item">
                      <span
                        className="dash-cartera-color"
                        style={{ background: s.color }}
                      />
                      {s.label}: {s.count}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ═════ ZONA 4: Atajos a lo que importa (Top 3 + Top 3) ═════ */}
      {can("deudas.ver") && (
        <section className="dashboard-section dash-atajos-section">
          <FinancialList
            title="Top 3 críticas"
            items={cobranza.rankings.criticas.slice(0, 3)}
            emptyText="Sin cuentas críticas 👌"
            renderItem={(deuda) => (
              <button
                key={deuda.id}
                type="button"
                className="financial-row"
                onClick={() =>
                  navigate(`/app/cliente/${deuda.clienteId}/deudas/${deuda.id}`)
                }
              >
                <span>
                  <strong>{deuda.clienteNombre}</strong>
                  <small>
                    {deuda.perfil.prioridad} · {deuda.perfil.mesesAtraso}m atraso
                  </small>
                </span>
                <span className="financial-amount bad">
                  {formatMoney(deuda.perfil.saldoAtrasado)}
                </span>
              </button>
            )}
          />

          <FinancialList
            title="Top 3 recuperables"
            items={cobranza.rankings.mayorRecuperabilidad.slice(0, 3)}
            emptyText="Sin oportunidades activas."
            renderItem={(deuda) => (
              <button
                key={deuda.id}
                type="button"
                className="financial-row"
                onClick={() =>
                  navigate(`/app/cliente/${deuda.clienteId}/deudas/${deuda.id}`)
                }
              >
                <span>
                  <strong>{deuda.clienteNombre}</strong>
                  <small>
                    {deuda.perfil.recuperabilidad} ·{" "}
                    {deuda.perfil.oportunidadLabel}
                  </small>
                </span>
                <span className="financial-amount good">
                  {formatMoney(deuda.perfil.pagoIdeal)}
                </span>
              </button>
            )}
          />

          <FinancialList
            title="Devoluciones pendientes"
            items={financiero.devoluciones.slice(0, 3)}
            emptyText="Sin devoluciones."
            renderItem={(deuda) => (
              <button
                key={deuda.id}
                type="button"
                className="financial-row"
                onClick={() =>
                  navigate(`/app/cliente/${deuda.clienteId}/deudas/${deuda.id}`)
                }
              >
                <span>
                  <strong>{deuda.clienteNombre}</strong>
                  <small>{deuda.folio || deuda.id}</small>
                </span>
                <span className="financial-amount warn">
                  {formatMoney(deuda.fin.pendienteDevolver)}
                </span>
              </button>
            )}
          />
        </section>
      )}

      {/* ═════ ZONA 5: KPIs secundarios ═════ */}
      {can("deudas.ver") && (
        <section className="dashboard-section">
          <div className="section-head">
            <h3>Detalle financiero</h3>
          </div>
          <div className="dash-kpi-mini-grid">
            <KpiCard
              label="Cobrado hoy"
              value={formatMoney(resumenFin.cobradoHoy)}
              className="positivo"
            />
            <KpiCard
              label="Por cobrar semana"
              value={formatMoney(resumenCobranza.porCobrarSemana)}
              className="mora"
            />
            <KpiCard
              label="Saldo pendiente"
              value={formatMoney(resumenFin.saldoPendiente)}
            />
            <KpiCard
              label="Monto prometido"
              value={formatMoney(operativo.montoPrometidoPendiente)}
              className="mora"
            />
            <KpiCard
              label="Devoluciones"
              value={formatMoney(resumenFin.pendienteDevolver)}
              className="mora"
            />
            <KpiCard
              label="Deudas activas"
              value={resumenFin.deudasActivas}
            />
            <KpiCard
              label="Liquidadas"
              value={resumenFin.deudasLiquidadas}
              className="positivo"
            />
            <KpiCard
              label="Gestiones hoy"
              value={operativo.gestionesHoy}
            />
          </div>
        </section>
      )}

      {/* ═════ ZONA 6: Accesos directos ═════ */}
      <section className="dashboard-section dash-accesos-section">
        <div className="section-head">
          <h3>Accesos directos</h3>
        </div>
        <div className="dash-accesos-grid">
          {can("cobranzaInteligente.ver") && (
            <button
              className="dash-acceso-card"
              onClick={() => navigate("/app/cobranza/inteligente")}
            >
              <span className="dash-acceso-icon">🎯</span>
              <span className="dash-acceso-text">Panel Inteligente</span>
            </button>
          )}
          {can("reportes.ver") && (
            <button
              className="dash-acceso-card"
              onClick={() => navigate("/app/reportes")}
            >
              <span className="dash-acceso-icon">📊</span>
              <span className="dash-acceso-text">Reportes Ejecutivos</span>
            </button>
          )}
          {can("agenda.ver") && (
            <button
              className="dash-acceso-card"
              onClick={() => navigate("/app/agenda/vencida")}
            >
              <span className="dash-acceso-icon">📅</span>
              <span className="dash-acceso-text">Agenda Vencida</span>
            </button>
          )}
          {can("deudas.ver") && (
            <button
              className="dash-acceso-card"
              onClick={() => navigate("/app/apartados")}
            >
              <span className="dash-acceso-icon">📌</span>
              <span className="dash-acceso-text">Apartados</span>
            </button>
          )}
          {can("comisiones.ver") && (
            <button
              className="dash-acceso-card"
              onClick={() => navigate("/app/comisiones")}
            >
              <span className="dash-acceso-icon">💼</span>
              <span className="dash-acceso-text">Comisiones</span>
            </button>
          )}
          {can("tickets.ver") && (
            <button
              className="dash-acceso-card"
              onClick={() => navigate("/app/tickets")}
            >
              <span className="dash-acceso-icon">🎫</span>
              <span className="dash-acceso-text">
                Tickets {ticketsUrgentes > 0 && (
                  <span className="dash-acceso-badge">{ticketsUrgentes}</span>
                )}
              </span>
            </button>
          )}
        </div>
      </section>

      {/* ═════ ZONA 7: Búsqueda rápida (compacta, al final) ═════ */}
      <section className="dashboard-section buscadores-section dash-busqueda-compacta">
        <div className="section-head">
          <h3>Búsqueda rápida</h3>
        </div>

        <div className="buscadores-grid">
          {can("clientes.ver") && (
            <SearchCard
              title="Buscar Cliente"
              placeholder="Escanee código C:... o escriba ID"
              value={buscarCliente}
              onChange={setBuscarCliente}
              onKeyDown={handleBuscarCliente}
              loading={loadingCliente}
              error={errorCliente}
            />
          )}
          {can("deudas.ver") && (
            <SearchCard
              title="Buscar Deuda"
              placeholder="Escanee código D:... o escriba ID"
              value={buscarDeuda}
              onChange={setBuscarDeuda}
              onKeyDown={handleBuscarDeuda}
              loading={loadingDeuda}
              error={errorDeuda}
            />
          )}
          {can("deudas.ver") && (
            <SearchCard
              title="Verificar Pago"
              placeholder="Escanee código P:..."
              value={buscarPago}
              onChange={setBuscarPago}
              onKeyDown={handleVerificarPago}
              loading={loadingPago}
              error={errorPago}
            />
          )}
        </div>

        {pagoVerificado && (
          <div className="pago-verificado-card">
            <div className="pago-verificado-header">
              ✅ Pago Verificado
              <button
                className="cerrar-verificado"
                onClick={() => setPagoVerificado(null)}
              >
                ✕
              </button>
            </div>
            <div className="pago-verificado-body">
              <div className="pago-info-row">
                <strong>Cliente:</strong>{" "}
                {pagoVerificado.cliente
                  ? `${pagoVerificado.cliente.nombre} ${pagoVerificado.cliente.apellidos}`.trim()
                  : "Desconocido"}
              </div>
              {pagoVerificado.cliente?.telefono && (
                <div className="pago-info-row">
                  <strong>Teléfono:</strong> {pagoVerificado.cliente.telefono}
                </div>
              )}
              <div className="pago-info-row">
                <strong>Deuda:</strong>{" "}
                {pagoVerificado.deuda.folio || pagoVerificado.deuda.id}
              </div>
              <div className="pago-info-row">
                <strong>Monto:</strong> {formatMoneyMx(pagoVerificado.pago?.cantidad)}
              </div>
              <div className="pago-info-row">
                <strong>Fecha:</strong> {formatDate(pagoVerificado.pago?.fecha)}
              </div>
              <div className="pago-info-row">
                <strong>Cobrado por:</strong> {pagoVerificado.cobrador}
              </div>
              {pagoVerificado.pago?.metodo && (
                <div className="pago-info-row">
                  <strong>Método:</strong> {pagoVerificado.pago.metodo}
                </div>
              )}
              {(pagoVerificado.pago?.notas || pagoVerificado.pago?.nota) && (
                <div className="pago-info-row">
                  <strong>Notas:</strong>{" "}
                  {pagoVerificado.pago?.notas || pagoVerificado.pago?.nota}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};


export default Dashboard;
